"use client";
import React from 'react';
import {
  ShieldCheck,
  Trophy,
  Lightbulb,
  Gauge,
  Users,
  Clock,
  Sparkles,
  ArrowRight
} from 'lucide-react';
import { motion } from 'framer-motion';

interface ValueCard {
  icon: React.ReactNode;
  title: string;
  description: string;
  color: string;
}

const values: ValueCard[] = [
  {
    icon: <Trophy className="h-8 w-8" aria-hidden="true" />,
    title: "Excellence",
    description: "We deliver enterprise-grade, AI-powered software solutions with unmatched quality and performance, designed to maximize ROI and drive measurable business growth.",
    color: "from-amber-500 to-yellow-400"
  },
  {
    icon: <ShieldCheck className="h-8 w-8" aria-hidden="true" />,
    title: "Accountability & Trust",
    description: "Our AI development process ensures transparent communication, data security, and 100% accountability—empowering businesses to scale with confidence.",
    color: "from-emerald-500 to-teal-400"
  },
  {
    icon: <Lightbulb className="h-8 w-8" aria-hidden="true" />,
    title: "Expertise",
    description: "Our team of senior AI engineers and data scientists brings deep expertise in machine learning, SaaS platforms, and cloud-based software development.",
    color: "from-blue-500 to-sky-400"
  },
  {
    icon: <Gauge className="h-8 w-8" aria-hidden="true" />,
    title: "Agile Innovation",
    description: "We rapidly adapt to evolving technologies and market demands, delivering scalable AI solutions that solve complex business challenges with speed and precision.",
    color: "from-purple-500 to-violet-400"
  },
  {
    icon: <Users className="h-8 w-8" aria-hidden="true" />,
    title: "Collaborative Partnership",
    description: "We work hand-in-hand with clients, aligning AI strategies with business objectives to create custom, results-driven solutions that outperform competitors.",
    color: "from-rose-500 to-pink-400"
  },
  {
    icon: <Clock className="h-8 w-8" aria-hidden="true" />,
    title: "Reliable Delivery",
    description: "With proven project management and on-time execution, we ensure every AI implementation meets deadlines, exceeds KPIs, and drives sustained success.",
    color: "from-indigo-500 to-blue-400"
  }
];


const WhyChooseUsSection = () => {
  return (
    <section id="why-choose-us" className="pb-24 relative overflow-hidden" aria-labelledby="why-choose-us-heading">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden opacity-30">
        <div className="absolute top-20 left-10 w-60 h-60 rounded-full bg-blue-400 filter blur-[80px] animate-float"></div>
        <div className="absolute bottom-10 right-10 w-80 h-80 rounded-full bg-indigo-400 filter blur-[100px] animate-float-delay"></div>
        <div className="absolute top-1/3 right-1/4 w-40 h-40 rounded-full bg-cyan-400 filter blur-[70px] animate-float"></div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center gap-2 mb-4 mt-4 px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full text-white text-sm font-medium border border-white/30 shadow-sm">
            <Sparkles className="h-4 w-4 " aria-hidden="true" />
            {/* <span>Why we stand out in AI innovation</span> */}
          </div>
          <h2 id="why-choose-us-heading" className="text-4xl md:text-5xl font-bold text-white mb-4">
            The <span className="bg-gradient-to-r from-cyan-400 to-white bg-clip-text text-transparent">GenAIPulse</span> Difference
          </h2>

        </motion.div>

        {/* Value cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
          {values.map((value, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1, duration: 0.5 }}
              viewport={{ once: true }}
              whileHover={{ y: -5 }}
              className="group relative bg-white/10 backdrop-blur-lg rounded-xl border border-white/20 shadow-lg hover:shadow-xl transition-all duration-300 p-6 flex flex-col overflow-hidden"
            >
              {/* Gradient accent */}
              <div className={`absolute inset-x-0 top-0 h-1 bg-gradient-to-r ${value.color}`}></div>
              <div className={`mb-6 rounded-lg bg-gradient-to-br ${value.color} w-12 h-12 flex items-center justify-center text-white shadow-md`}>
                {value.icon}
              </div>
              <h3 className="text-xl font-bold text-white mb-3">{value.title}</h3>
              <p className="text-blue-100 text-sm mb-6">{value.description}</p>
            </motion.div>
          ))}
        </div>

        {/* Commitment Section */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          viewport={{ once: true }}
          className="mt-20 backdrop-blur-md rounded-2xl p-10 md:p-14 text-center text-white max-w-5xl mx-auto shadow-2xl relative overflow-hidden border border-white/30"
        >
          {/* Decorative pattern */}
          <div className="absolute inset-0 opacity-20">
            <div className="absolute top-0 right-0 w-32 h-32 bg-cyan-400 rounded-full blur-2xl"></div>
            <div className="absolute bottom-0 left-0 w-40 h-40 bg-indigo-400 rounded-full blur-2xl"></div>
          </div>
          <div className="relative z-10">
            <h3 className="text-3xl md:text-4xl font-bold mb-5">
              Our Commitment to <span className="text-yellow-300">Your Success</span>
            </h3>
            <p className="text-blue-100 text-lg mb-8 max-w-3xl mx-auto">
              When you choose <strong>GenAIPulse</strong>, you&apos;re selecting a partner dedicated to long-term results in AI. 
              We don&apos;t just build solutions — we build relationships founded on trust, innovation, and measurable achievements.
            </p>
            <motion.a
              href="/contact"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="inline-flex items-center justify-center bg-white/90 text-blue-700 font-semibold py-3 px-8 rounded-lg shadow-md hover:bg-white transition duration-300 text-lg"
            >
              Start Your Journey With Us
              <ArrowRight className="ml-2 h-5 w-5" aria-hidden="true" />
            </motion.a>
          </div>
        </motion.div>
      </div>

      {/* Floating animation styles */}
      <style jsx global>{`
        @keyframes float {
          0%, 100% {
            transform: translateY(0) translateX(0);
          }
          50% {
            transform: translateY(-20px) translateX(10px);
          }
        }
        .animate-float {
          animation: float 8s ease-in-out infinite;
        }
        .animate-float-delay {
          animation: float 10s ease-in-out infinite 2s;
        }
      `}</style>
    </section>
  );
};

export default WhyChooseUsSection;
